import { Formik } from "formik";
import React, { useEffect, useRef } from "react";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Modal from "../../../../components/modal";
import { saveGoals } from "../../../../../api";
import FancyCheckBox from "../../../../components/form/fancy-checkbox";
import TextInput from "../../../../components/form/text-input";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import Button from "../../../../components/new-button";
import useDashboardStore, { fetchGoalData } from "../../../../stores/dashboard";
import useUserStore from "../../../../stores/user";
import baseStyles from "../../../../pages/create-app/components/application-form/base.module.scss";
import styles from "./styles.module.scss";

const SetDailyGoalModal = () => {
  const abortController = useRef(null);
  const open = useDashboardStore((state) => state.dailyGoalModalOpen);
  //prettier-ignore
  const agentGoalData = useDashboardStore((state) => state.goalData?.agentGoalData);
  const orgGoalData = useDashboardStore((state) => state.goalData?.orgGoalData);
  const setDailyGoalModalOpen = useDashboardStore(
    (state) => state.setDailyGoalModalOpen,
  );
  const goalFilterKey = useDashboardStore((s) => s.goalFilterKey);
  const isAdmin = useUserStore((state) => state.isAdmin);

  const handleSetDailyGoalFormSubmit = (values, { setSubmitting }) => {
    abortController.current = new AbortController();
    setSubmitting(true);
    toast.loading("Saving goal data...");
    saveGoals(values)
      .then((res) => {
        if (res.status) {
          toast.dismiss();
          toast.success("Goal set successfully");
          setDailyGoalModalOpen(false);
          fetchGoalData(
            goalFilterKey ? { user: goalFilterKey } : {},
            abortController.current,
            false,
          );
        } else {
          toast.error(res?.message || "Error setting goal");
        }
      })
      .catch((err) => {
        toast.dismiss();
        toast.error(err?.message || "Error setting goal");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  const goalOptions = [
    {
      id: "agent",
      label: "Agent",
    },
    {
      id: "org",
      label: "Organization",
    },
  ];

  const handleGoalChange = (selected, setFieldValue) => {
    const selectedData = selected === "agent" ? agentGoalData : orgGoalData;
    setFieldValue("goalUser", selected);
    setFieldValue("submitting_apps", selectedData?.submitting_apps);
    setFieldValue("approved_apps", selectedData?.approved_apps);
    setFieldValue("monthly_volume", selectedData?.monthly_volume);
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => setDailyGoalModalOpen(false)}
      header="Set daily goal"
      withAlert
      size="sm"
    >
      <Formik
        enableReinitialize
        validationSchema={Yup.object().shape({
          submitting_apps: Yup.number().required("Submitted apps is required"),
          approved_apps: Yup.number().required("Approved apps is required"),
          monthly_volume: Yup.number().required("Monthly volume is required"),
          goalUser: Yup.string().required("Goal user is required"),
        })}
        onSubmit={handleSetDailyGoalFormSubmit}
        initialValues={{
          submitting_apps: agentGoalData?.submitting_apps,
          approved_apps: agentGoalData?.approved_apps,
          monthly_volume: agentGoalData?.monthly_volume,
          goalUser: "agent",
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
              <div className={`${styles.grid} ${styles.gap3}`}>
                {isAdmin && (
                  <>
                    <HeadingWithLine text="Set goal for" line={false} />
                    <div
                      className={`${baseStyles.grid} ${baseStyles.gridCol2} ${baseStyles.gap1}`}
                    >
                      {goalOptions.map((option) => (
                        <FancyCheckBox
                          key={option.id}
                          id={option.id}
                          name={option.id}
                          label={option.label}
                          checked={values?.goalUser == option.id}
                          onChange={(e) =>
                            handleGoalChange(option.id, setFieldValue)
                          }
                          width="100%"
                        />
                      ))}
                    </div>
                  </>
                )}

                <HeadingWithLine text="Goals" line={false} />

                <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
                  <TextInput
                    label="Submitted Apps"
                    id="submitting_apps"
                    name="submitting_apps"
                    value={values.submitting_apps}
                    onChange={handleChange}
                    touched={touched.submitting_apps}
                    errored={errors.submitting_apps}
                    error={errors["submitting_apps"]}
                    cleaveOptions={{
                      numericOnly: true,
                      blocks: [10],
                      onValueChanged: (e) => {
                        setFieldValue("submitting_apps", e.target.value);
                      },
                    }}
                  />
                  <TextInput
                    label="Approved Apps"
                    id="approved_apps"
                    name="approved_apps"
                    value={values.approved_apps}
                    onChange={handleChange}
                    touched={touched.approved_apps}
                    errored={errors.approved_apps}
                    error={errors["approved_apps"]}
                    cleaveOptions={{
                      numericOnly: true,
                      blocks: [10],
                      onValueChanged: (e) => {
                        setFieldValue("approved_apps", e.target.value);
                      },
                    }}
                  />
                  <TextInput
                    label="Monthly Volume"
                    id="monthly_volume"
                    name="monthly_volume"
                    value={values.monthly_volume}
                    onChange={handleChange}
                    touched={touched.monthly_volume}
                    errored={errors.monthly_volume}
                    error={errors["monthly_volume"]}
                    cleaveOptions={{
                      numericOnly: true,
                      blocks: [10],
                      onValueChanged: (e) => {
                        setFieldValue("monthly_volume", e.target.value);
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
              <div
                className={`${baseStyles.flex} ${baseStyles.flexEnd} ${baseStyles.gap1}`}
                style={{ marginTop: "16px" }}
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setDailyGoalModalOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default SetDailyGoalModal;
